/**
 * Created by Evžen Rejfíř on 22.07.2025.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import Relay from 'react-relay';
import AppPanel from '../fw/AppPanel.react';
import EnqueueMonthlyTariffClickUpJob from '../admin/EnqueueMonthlyTariffClickUpJob.react';
import EnqueueMonthlyTariffClickUpAllJob from '../admin/EnqueueMonthlyTariffClickUpAllJob.react';
import showNotification, {showJobNotification} from '../app/notification.js';
import LoadingButton from '../fw/LoadingButton.react';
import Button from 'react-bootstrap/lib/Button';
import Modal from 'react-bootstrap/lib/Modal';
import Table from 'react-bootstrap/lib/Table';
import Label from 'react-bootstrap/lib/Label';
import { wrapNotFound } from '../app/NotFoundHandler.react';
import { processPagingParams } from '../fw/PagingGrid.react';
import urlMapping from '../app/URLMapping';
import types from '../../common/model-api/types';
import * as BrowserPermission from '../BrowserPermission';
import './TariffList.scss';
import moment from 'moment';

const messages = defineMessages({
    generateForAll: {
        id: 'tariffs.generateForAll',
        defaultMessage: 'Generate tasks for all organizations'
    },
    generateForThisOrg: {
        id: 'tariffs.generateForThisOrg',
        defaultMessage: 'Generate task for this organization'
    },
    title: {
        id: 'tariffs.title',
        defaultMessage: 'Tariffs'
    },
    addTariff: {
        id: 'tariffs.addTariff',
        defaultMessage: 'Add Tariff'
    },
    noTariffs: {
        id: 'tariffs.noTariffs',
        defaultMessage: 'No tariffs found'
    },
    tariffType: {
        id: 'tariffs.tariffType',
        defaultMessage: 'Tariff Type'
    },
    startDate: {
        id: 'tariffs.startDate',
        defaultMessage: 'Start Date'
    },
    endDate: {
        id: 'tariffs.endDate',
        defaultMessage: 'End Date'
    },
    status: {
        id: 'tariffs.status',
        defaultMessage: 'Status'
    },
    customerNumber: {
        id: 'tariffs.customerNumber',
        defaultMessage: 'Customer Number'
    },
    tariffNumber: {
        id: 'tariffs.tariffNumber',
        defaultMessage: 'Tariff Number'
    },
    actions: {
        id: 'tariffs.actions',
        defaultMessage: 'Actions'
    },
    view: {
        id: 'tariffs.view',
        defaultMessage: 'View'
    },
    edit: {
        id: 'tariffs.edit',
        defaultMessage: 'Edit'
    },
    active: {
        id: 'tariffs.active',
        defaultMessage: 'Active'
    },
    inactive: {
        id: 'tariffs.inactive',
        defaultMessage: 'Inactive'
    },
    onlyOneActiveTariff: {
        id: 'tariffs.onlyOneActiveTariff',
        defaultMessage: 'Only one tariff can be active per organization. Please deactivate the current active tariff first.'
    },
    selectMonthDialogTitle: {
        id: 'tariffs.selectMonthDialogTitle',
        defaultMessage: 'Generate ClickUp Task'
    },
    selectMonthDialogMessage: {
        id: 'tariffs.selectMonthDialogMessage',
        defaultMessage: 'For which month do you want to create the task?'
    },
    currentMonth: {
        id: 'tariffs.currentMonth',
        defaultMessage: 'Current month'
    },
    nextMonth: {
        id: 'tariffs.nextMonth',
        defaultMessage: 'Next month'
    },
    cancel: {
        id: 'common.cancel',
        defaultMessage: 'Cancel'
    },
    generate: {
        id: 'tariffs.generate',
        defaultMessage: 'Generate'
    }
});

class TariffList extends React.Component {
    static propTypes = {
        intl: PropTypes.object.isRequired,
        params: PropTypes.object.isRequired,
        user: PropTypes.object.isRequired,
    };

    static contextTypes = {
        router: PropTypes.object
    };

    constructor(props) {
        super(props);
        this.state = {
            generating: false,
            notice: null,
            showMonthDialog: false,
            selectedTargetMonth: 'next', // 'current' or 'next'
            dialogMode: 'single' // 'single' or 'all'
        };
    }

    showMonthSelectionDialog = (mode = 'single') => {
        this.setState({ showMonthDialog: true, selectedTargetMonth: 'next', dialogMode: mode });
    };

    hideMonthDialog = () => {
        this.setState({ showMonthDialog: false });
    };

    handleMonthSelect = (targetMonth) => {
        this.setState({ selectedTargetMonth: targetMonth });
    };

    showMonthSelectionDialogForAll = () => {
        this.showMonthSelectionDialog('all');
    };

    onClickGenerateClickUp = () => {
        const { intl } = this.props;
        const { selectedTargetMonth, dialogMode } = this.state;

        return new Promise((resolve, reject) => {
            try {
                if (dialogMode === 'all') {
                    // Generate for all organizations
                    Relay.Store.commitUpdate(new EnqueueMonthlyTariffClickUpAllJob({
                        targetMonth: selectedTargetMonth
                    }), {
                        onFailure: () => {
                            showNotification('Failed to start job', 'See console for details');
                            reject();
                        },
                        onSuccess: (response) => {
                            const job = response && response.enqueueMonthlyTariffClickUpAllJob && response.enqueueMonthlyTariffClickUpAllJob.job;
                            if (job) {
                                showJobNotification(intl, job, 'Generating ClickUp tasks for all organizations…');
                            }
                            this.hideMonthDialog();
                            resolve();
                        }
                    });
                } else {
                    // Generate for single organization
                    const orgId = this.props.user.organisation.realId;
                    Relay.Store.commitUpdate(new EnqueueMonthlyTariffClickUpJob({
                        organisationId: orgId,
                        targetMonth: selectedTargetMonth
                    }), {
                        onFailure: () => {
                            showNotification('Failed to start job', 'See console for details');
                            reject();
                        },
                        onSuccess: (response) => {
                            const job = response && response.enqueueMonthlyTariffClickUpJob && response.enqueueMonthlyTariffClickUpJob.job;
                            if (job) {
                                showJobNotification(intl, job, 'Generating ClickUp task for this organization…');
                            }
                            this.hideMonthDialog();
                            resolve();
                        }
                    });
                }
            } catch (e) {
                showNotification('Failed to start job', (e && e.message) ? e.message : 'Unknown error');
                reject(e);
            }
        });
    };

    // This function is no longer used directly, replaced by dialog-based flow
    // Kept for backward compatibility if needed
    onClickGenerateAll = () => {
        // Now opens dialog instead of directly executing
        this.showMonthSelectionDialogForAll();
        return Promise.resolve();
    };

    isTariffActive(tariff) {
        if (!tariff || !tariff.startDate) {
            return false;
        }

        const now = moment();
        const startDate = moment(tariff.startDate);
        const endDate = tariff.endDate ? moment(tariff.endDate) : null;

        if (now.isBefore(startDate)) return false;
        if (endDate && now.isAfter(endDate)) return false;
        return true;
    }

    hasActiveTariff = () => {
        const { user } = this.props;
        const organisation = user.organisation;

        if (!organisation || !organisation.tariffs || !organisation.tariffs.edges) {
            return false;
        }

        return organisation.tariffs.edges.some(edge => {
            const tariff = edge.node;
            return this.isTariffActive(tariff);
        });
    };

    handleAddTariff = () => {
        const { params } = this.props;
        const url = urlMapping.getUrl(types['tariffs'], 'create', { id: params.organisationId });
        this.context.router.push(url);
    };

    handleEditTariff = (tariffId) => {
        const { params } = this.props;
        const url = urlMapping.getUrl(types['tariffs'], 'edit', { organisation_id: params.organisationId, id: tariffId });
        this.context.router.push(url);
    };

    handleViewTariff = (tariffId) => {
        const { params } = this.props;
        const url = urlMapping.getUrl(types['tariffs'], 'view', { organisation_id: params.organisationId, id: tariffId });
        this.context.router.push(url);
    };

    renderStatusLabel = (tariff) => {
        const { intl } = this.props;

        // Use dynamic calculation instead of database field
        const isActive = this.isTariffActive(tariff);
        return isActive ?
            <Label bsStyle="success">{intl.formatMessage(messages.active)}</Label> :
            <Label bsStyle="warning">{intl.formatMessage(messages.inactive)}</Label>;
    };

    renderTariffTable = () => {
        const { user } = this.props;
        const organisation = user.organisation;

        // No need for null check here since wrapNotFound handles it
        const tariffs = organisation.tariffs ? organisation.tariffs.edges : [];

        return (
            <Table bordered hover responsive striped>
                <thead>
                    <tr>
                        <th><FormattedMessage {...messages.tariffType} /></th>
                        <th><FormattedMessage {...messages.startDate} /></th>
                        <th><FormattedMessage {...messages.endDate} /></th>
                        <th><FormattedMessage {...messages.status} /></th>
                        <th><FormattedMessage {...messages.customerNumber} /></th>
                        <th><FormattedMessage {...messages.tariffNumber} /></th>
                        {BrowserPermission.isDeveloper(this.props.user.organisation && this.props.user.organisation.realId) ? (
                            <th><FormattedMessage {...messages.actions} /></th>
                        ) : null}
                    </tr>
                </thead>
                <tbody>
                    {tariffs.length === 0 ? (
                        <tr>
                            <td className="text-center" colSpan={BrowserPermission.isDeveloper(this.props.user.organisation && this.props.user.organisation.realId) ? 7 : 6}>
                                <FormattedMessage {...messages.noTariffs} />
                            </td>
                        </tr>
                    ) : (
                        tariffs.map((edge) => {
                            const tariff = edge.node;
                            return (
                                <tr key={tariff.id}>
                                    <td>{tariff.tariffType}</td>
                                    <td>{tariff.startDate}</td>
                                    <td>{tariff.endDate || '-'}</td>
                                    <td>{this.renderStatusLabel(tariff)}</td>
                                    <td>{tariff.customerNumber}</td>
                                    <td>{tariff.tariffNumber}</td>
                                    {BrowserPermission.isDeveloper(this.props.user.organisation && this.props.user.organisation.realId) ? (
                                        <td>
                                            <Button
                                                bsSize="xs"
                                                onClick={() => this.handleViewTariff(tariff.realId)}
                                                style={{ marginRight: '5px' }}
                                            >
                                                <FormattedMessage {...messages.view} />
                                            </Button>
                                            <Button
                                                bsSize="xs"
                                                bsStyle="primary"
                                                onClick={() => this.handleEditTariff(tariff.realId)}
                                            >
                                                <FormattedMessage {...messages.edit} />
                                            </Button>
                                        </td>
                                    ) : null}
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </Table>
        );
    };

    render() {
        const { intl } = this.props;
        const hasActiveTariff = this.hasActiveTariff();

        // No need for null check here since wrapNotFound handles it
        return (
            <AppPanel title={intl.formatMessage(messages.title)}>
                <div className="panel-body">
                    {this.state && this.state.notice ? (
                        <div className={`alert alert-${this.state.notice.type === 'success' ? 'success' : 'danger'}`} role="alert" style={{ marginBottom: '15px' }}>
                            {this.state.notice.message}
                        </div>
                    ) : null}
                    <div className="row">
                        <div className="col-md-12">
                            {BrowserPermission.isDeveloper(this.props.user.organisation && this.props.user.organisation.realId) ? (
                                <div className="pull-right" style={{ marginBottom: '20px' }}>
                                    <LoadingButton className="btn btn-default" onClick={this.onClickGenerateAll} style={{ marginRight: '10px' }}>
                                        <FormattedMessage {...messages.generateForAll} />
                                    </LoadingButton>
                                    <LoadingButton className="btn btn-success" onClick={this.showMonthSelectionDialog} style={{ marginRight: '10px' }}>
                                        <FormattedMessage {...messages.generateForThisOrg} />
                                    </LoadingButton>
                                    <Button
                                        bsStyle="primary"
                                        disabled={hasActiveTariff}
                                        onClick={this.handleAddTariff}
                                        title={hasActiveTariff ? intl.formatMessage(messages.onlyOneActiveTariff) : ''}
                                    >
                                        <FormattedMessage {...messages.addTariff} />
                                    </Button>
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            {this.renderTariffTable()}
                        </div>
                    </div>

                    {/* Month Selection Dialog */}
                    <Modal onHide={this.hideMonthDialog} show={this.state.showMonthDialog}>
                        <Modal.Header closeButton>
                            <Modal.Title><FormattedMessage {...messages.selectMonthDialogTitle} /></Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p><FormattedMessage {...messages.selectMonthDialogMessage} /></p>
                            <div className="radio">
                                <label>
                                    <input
                                        checked={this.state.selectedTargetMonth === 'current'}
                                        onChange={() => this.handleMonthSelect('current')}
                                        type="radio"
                                        value="current"
                                    />
                                    {' '}
                                    <FormattedMessage {...messages.currentMonth} />
                                    {' '}
                                    <strong>({moment().format('MM/YYYY')})</strong>
                                </label>
                            </div>
                            <div className="radio">
                                <label>
                                    <input
                                        checked={this.state.selectedTargetMonth === 'next'}
                                        onChange={() => this.handleMonthSelect('next')}
                                        type="radio"
                                        value="next"
                                    />
                                    {' '}
                                    <FormattedMessage {...messages.nextMonth} />
                                    {' '}
                                    <strong>({moment().add(1, 'month').format('MM/YYYY')})</strong>
                                    {' '}
                                    <span className="text-muted">[výchozí]</span>
                                </label>
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button onClick={this.hideMonthDialog}>
                                <FormattedMessage {...messages.cancel} />
                            </Button>
                            <LoadingButton className="btn btn-success" onClick={this.onClickGenerateClickUp}>
                                <FormattedMessage {...messages.generate} />
                            </LoadingButton>
                        </Modal.Footer>
                    </Modal>
                </div>
            </AppPanel>
        );
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(TariffList), (props) => {
    return props.user.organisation;
}), {
    initialVariables: {
        first: 100,
        organisationId: '',
        jobId: ''
    },
    prepareVariables(variables) {
        processPagingParams(variables);
        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                id,
                job(id: $jobId) {
                    id,
                    realId,
                    status,
                    message
                },
                organisation(id: $organisationId) {
                    id,
                    realId,
                    name,
                    tariffs(first: $first) {
                        edges {
                            node {
                                id,
                                realId,
                                tariffType,
                                startDate,
                                endDate,
                                active,
                                customerNumber,
                                tariffNumber,
                                createdAt,
                                updatedAt
                            }
                        }
                    }
                }
            }
        `,
    },
});
